import React, { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment, { Moment } from 'moment';
import {
  Button, Col, DatePicker, Divider, Row, Space, TimePicker,
} from 'antd';
import axios from 'axios';
import Papa from 'papaparse';
import { DAYS_OF_WEEK, getDaysOfWeekString, timezoneToUtc } from '../../util/tstamp';
import PPDMap from './components/PPDMap';
import PPDChart from './components/PPDChart';
import PPDHistogram from './components/PPDHistogram';
import PhaseDiagram from './components/PhaseDiagram';
import PhasePPD from './components/PhasePPD';
import PhaseButton from './components/PhaseButton';
import { getAggDataURL, getPPDDataURL } from '../../util/urlApi';
import {
  DATETIME_FORMAT,
  IS_DEV_VERSION,
  TIME_FORMAT,
  TIME_FORMAT_WITH_SECONDS,
} from '../../util/constants';
import { AggDataElement } from '../../types/AggDataElement';
import { convertToAggData } from '../../util/util';
import { Direction } from '../../types/Direction';
import { Movement } from '../../types/Movement';
import { IntersectionId } from '../../types/primitives';
import { ChartType } from '../../types/ChartType';
import { AggDataResponseItem } from '../../types/Response';
import { DayOfWeek } from '../../types/DayOfWeek';

const { RangePicker } = DatePicker;

interface PPDPageParams {
  startDate: string;
  endDate: string;
  todStart: string;
  todEnd: string;
  selectedDaysOfWeek: string;
  intersectionId: string;
  intersectionName: string;
  timezone: string;
  direction: string;
  movement: string;
  lat: string;
  long: string;
}

/**
 * This function returns the page that consist of two components:
 * PPD  - PPD diagram
 * PPDMap - google map pointing to the study position
 */
const PPDPage = (): ReactElement => {
  const params = useParams<PPDPageParams>();
  // variables from the url parameters
  const {
    startDate,
    endDate,
    todStart,
    todEnd,
  } = params;
  // const { endDate } = params;
  // const { todStart } = params;
  // const { todEnd } = params;
  const selectedDaysOfWeek = DAYS_OF_WEEK.filter((d) => (
    params.selectedDaysOfWeek
      .split(',')
      .map((s) => parseInt(s))
      .includes(d.value)));
  const intersectionId: IntersectionId = parseInt(params.intersectionId) as IntersectionId;
  const intersectionName = decodeURIComponent(params.intersectionName);
  const timezone = decodeURIComponent(params.timezone);
  const direction: Direction = params.direction as Direction;
  const movement: Movement = params.movement as Movement;
  const { lat } = params;
  const { long } = params;

  // const [showDetail, setShowDetail] = useState(false);
  // const [, setShowDetailButtonLabel] = useState('Show Detail');
  const [startDateSelected, setStartDateSelected] = useState(startDate);
  const [endDateSelected, setEndDateSelected] = useState(endDate);
  const [todStartSelected, setTodStartSelected] = useState(todStart);
  const [todEndSelected, setTodEndSelected] = useState(todEnd);
  const [startDateConfig, setStartDateConfig] = useState(startDate);
  const [endDateConfig, setEndDateConfig] = useState(endDate);
  const [todStartConfig, setTodStartConfig] = useState(todStart);
  const [todEndConfig, setTodEndConfig] = useState(todEnd);
  const [aggData, setAggData] = useState<AggDataElement[]>([]);
  const [ppdData, setPPDData] = useState<[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const apiUrl = getPPDApiUrl(startDate, endDate, intersectionId, timezone,
    direction, movement, selectedDaysOfWeek, todStart, todEnd);
  const histogramApiUrl = getAggDataApiUrl(startDate, endDate, intersectionId, timezone,
    direction, movement, selectedDaysOfWeek, todStart, todEnd);
  const pageTitle = `${intersectionName} - ${direction} - ${getDaysOfWeekString(selectedDaysOfWeek)}`;
  // const pageSubTitle = `${startDateSelected.split(' ')[0]} -
  // ${endDateSelected.split(' ')[0]} | ${todStartSelected} ~ ${todEndSelected}`;
  const chartTitle = `${intersectionName} - ${direction} ${todStartSelected} ~ ${todEndSelected}`;
  const sessionId = sessionStorage.getItem('sessionId');

  // Make the title 'DEV ...' if it is
  if (IS_DEV_VERSION) {
    document.title = 'DEV PPD - PM Heatmap';
  } else {
    document.title = 'PPD - PM Heatmap';
  }
  // const toggleAllPhase = () => {
  //   if (showDetail) {
  //     setShowDetail(false);
  //     setShowDetailButtonLabel('Show Detail');
  //     const el = document.getElementById('all-phase-ppd');
  //     if (el) el.style.display = 'none';
  //   } else {
  //     setShowDetail(true);
  //     setShowDetailButtonLabel('Hide');
  //     const el = document.getElementById('all-phase-ppd');
  //     if (el) el.style.display = 'block';
  //   }
  // };

  useEffect(() => {
    refreshPPD();
  }, []);

  const refreshPPD = async () => {
    setStartDateConfig(startDateSelected);
    setEndDateConfig(endDateSelected);
    setTodStartConfig(todStartSelected);
    setTodEndConfig(todEndSelected);

    const promises = [];
    setIsLoading(true);

    const ppdDataApiUrl = getPPDApiUrl(startDateSelected, endDateSelected, intersectionId,
      timezone, direction, movement, selectedDaysOfWeek, todStartSelected, todEndSelected);
    promises.push(getPPDData(ppdDataApiUrl));

    const aggDataApiUrl = getAggDataApiUrl(startDateSelected, endDateSelected, intersectionId,
      timezone, direction, movement, selectedDaysOfWeek, todStartSelected, todEndSelected);
    promises.push(getTrajData(aggDataApiUrl));

    await Promise.all(promises);
    setIsLoading(false);
  };

  const onDateChange = (dates: unknown) => {
    const [start, end] = (dates as [Moment, Moment])
      .map((e) => moment(e)
        .format(DATETIME_FORMAT));
    setStartDateSelected(start);
    setEndDateSelected(end);
  };

  const onTimeOfDayChange = (times: unknown) => {
    const [start, end] = (times as [Moment, Moment])
      .map((e) => moment(e)
        .format(TIME_FORMAT_WITH_SECONDS));
    setTodStartSelected(start);
    setTodEndSelected(end);
  };

  const getTrajData = async (url: string) => {
    setIsLoading(true);
    const result = await axios.get(url, { headers: { Authorization: `Bearer ${sessionId}` } });
    setIsLoading(false);
    const ret = Papa.parse<AggDataResponseItem>(result.data, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
    }).data;
    setAggData(convertToAggData(ret));
  };

  const getPPDData = async (url: string) => {
    const result = await axios.get(url, { headers: { Authorization: `Bearer ${sessionId}` } });
    const ret = Papa.parse(result.data, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
    }).data as [];
    setPPDData(ret);
  };

  return (
    <div style={{
      marginTop: '1em',
      marginLeft: '1em',
      width: '100%',
    }}
    >
      <Row style={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
      >
        <h1>{pageTitle}</h1>
      </Row>
      {/* <Row style={{alignItems: 'center', justifyContent: 'center'}}> */}
      {/*    <h3>{pageSubTitle}</h3> */}
      {/* </Row> */}
      <Row style={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
      >
        <Space>
          <Space direction="vertical">
            <span className="input-label">Dates</span>

            <RangePicker
              value={[moment(startDateSelected), moment(endDateSelected)]}
              style={{ flex: '1 1 0' }}
              // disabledDate={disabledDate}
              ranges={{
                'This Week': [moment()
                  .startOf('week'), moment()],
                'Last Week': [
                  moment()
                    .subtract(1, 'week')
                    .startOf('week'),
                  moment()
                    .subtract(1, 'week')
                    .endOf('week'),
                ],
                'This Month': [moment()
                  .startOf('month'), moment()],
                'Last Month': [
                  moment()
                    .subtract(1, 'month')
                    .startOf('month'),
                  moment()
                    .subtract(1, 'month')
                    .endOf('month'),
                ],
                'August 2020': [moment('aug 2020')
                  .startOf('month'), moment('aug 2020')
                  .endOf('month')],
              }}
              onChange={onDateChange}
            />
          </Space>
          {/* <Space direction="vertical"> */}
          {/*    <DatePicker */}
          {/*        id='startDateTime' */}
          {/*        value={moment(startDateSelected)} */}
          {/*        style={{width: '130px'}} */}
          {/*        onChange={onDateBeginChange}/> */}
          {/* </Space> */}
          {/* <Space direction="vertical"> */}
          {/*    <span className="input-label">End Date:</span> */}
          {/*    <DatePicker */}
          {/*        id='endDateTime' */}
          {/*        value={moment(endDateSelected)} */}
          {/*        style={{width: '130px'}} */}
          {/*        onChange={onDateEndChange}/> */}
          {/* </Space> */}
          <Divider />
          <Space direction="vertical">
            <span className="input-label">Time of Day</span>
            <TimePicker.RangePicker
              value={[
                moment(todStartSelected, TIME_FORMAT_WITH_SECONDS),
                moment(todEndSelected, TIME_FORMAT_WITH_SECONDS),
              ]}
              onChange={onTimeOfDayChange}
              format={TIME_FORMAT}
            />
          </Space>
          <Button type="primary" onClick={refreshPPD} style={{ marginTop: '30px' }}>Refresh</Button>
        </Space>
      </Row>

      <Space
        wrap
        style={{
          margin: '1em',
          justifyContent: 'space-evenly',
          width: '100%',
        }}
      >
        {(['aog', 'sf', 'dsb', 'los'] as ChartType[]).map((type) => (
          <PPDHistogram
            key={type}
            histogramType={type}
            isLoading={isLoading}
            // apiUrl={histogramApiUrl}
            aggData={aggData}
            style={{
              width: '800px',
              height: '400px',
            }}
          />
        ))}
      </Space>
      <Row style={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
      >
        <Col style={{
          width: '900px',
          height: '850px',
        }}
        >
          <PPDChart
            ppdMain
            ppdType="main"
            apiUrl={apiUrl}
            apiUrlAgg={histogramApiUrl}
            direction={direction}
            aggData={aggData}
            chartTitle={chartTitle}
            chartWidth={800}
            chartHeight={800}
            ppdData={ppdData}
            isLoading={isLoading}
          />
        </Col>
        <Col style={{
          marginTop: '100px',
          width: '800px',
          height: '800px',
        }}
        >
          <PPDMap
            center={{
              lat: parseFloat(lat),
              lng: parseFloat(long),
            }}
            startDate={startDateSelected}
            endDate={endDateSelected}
            selectedDaysOfWeek={selectedDaysOfWeek}
            todStart={todStartSelected}
            todEnd={todEndSelected}
          />
        </Col>
      </Row>
      <Row
        id="all-phase-ppd"
        style={{
          // marginTop: '150px',
          marginTop: '1em',
          height: '100px',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {getAllPhasePPD(startDateConfig, endDateConfig, intersectionId, timezone,
          selectedDaysOfWeek, todStartConfig, todEndConfig, ppdData, aggData, isLoading)}
      </Row>
    </div>
  );
};

const getAllPhasePPD = (startDate: string, endDate: string, intersectionId: IntersectionId,
  timezone: string, selectedDaysOfWeek: DayOfWeek[], todStart: string, todEnd: string,
  ppdData: [], aggData: AggDataElement[], isLoading: boolean) => {
  const phases1 = [1, 2, 3, 4];
  const phases2 = [5, 6, 7, 8];
  const config = {
    startDate,
    endDate,
    intersectionId,
    intersectionTimezone: timezone,
    // TODO For now, I'm just using the value numbers.
    //  When I eventually redo the PhasePPD, I'll convert this.
    checkedDow: selectedDaysOfWeek.map((d) => d.value),
    todStart,
    todEnd,
  };
  return (
    <>
      <Row style={{
        width: '800px',
        height: '500px',
        display: 'none',
      }}
      >
        <Row style={{
          width: '100%',
          height: '45%',
        }}
        >
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={1} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          >
            <PhaseButton index={1} style={{ width: '100%' }} />
          </Col>
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={2} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          >
            <PhaseButton index={2} style={{ width: '100%' }} />
          </Col>
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={3} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          >
            <PhaseButton index={3} style={{ width: '100%' }} />
          </Col>
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={4} />
          </Col>
        </Row>
        <Row style={{
          width: '100%',
          height: '10%',
        }}
        >
          <Col style={{ width: '20%' }}>
            <PhaseButton marginLeft="75px" index={8} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          />
          <Col style={{ width: '20%' }}>
            <PhaseButton marginLeft="75px" index={9} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          />
          <Col style={{ width: '20%' }}>
            <PhaseButton marginLeft="75px" index={10} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          />
          <Col style={{ width: '20%' }}>
            <PhaseButton marginLeft="75px" index={11} />
          </Col>
        </Row>
        <Row style={{
          width: '100%',
          height: '45%',
        }}
        >
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={5} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          >
            <PhaseButton index={5} style={{ width: '100%' }} />
          </Col>
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={6} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          >
            <PhaseButton index={6} style={{ width: '100%' }} />
          </Col>
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={7} />
          </Col>
          <Col style={{
            width: '5%',
            marginTop: '12.5%',
          }}
          >
            <PhaseButton index={7} style={{ width: '100%' }} />
          </Col>
          <Col style={{ width: '20%' }}>
            <PhaseDiagram index={8} />
          </Col>
        </Row>
      </Row>
      <Row style={{
        width: '2000px',
        height: '1500px',
      }}
      >
        <Row style={{
          width: '100%',
          height: '50%',
        }}
        >
          {phases1.map((phase) => (
            <Col key={phase} style={{ width: '25%' }}>
              <PhasePPD
                index={phase}
                config={config}
                ppdData={ppdData}
                aggData={aggData}
                isLoading={isLoading}
              />
            </Col>
          ))}
        </Row>
        <Row style={{
          width: '100%',
          height: '50%',
        }}
        >
          {phases2.map((phase) => (
            <Col key={phase} style={{ width: '25%' }}>
              <PhasePPD
                index={phase}
                config={config}
                ppdData={ppdData}
                aggData={aggData}
                isLoading={isLoading}
              />
            </Col>
          ))}
        </Row>
      </Row>
    </>
  );
};

// @ts-ignore
const getPPDApiUrl = (startDate: string, endDate: string, intersectionId: IntersectionId,
  timezone: string, direction: Direction, movement: Movement, selectedDaysOfWeek: DayOfWeek[],
  todStart: string, todEnd: string): string => {
  const startDateUTC = timezoneToUtc(startDate, timezone)
    .format(DATETIME_FORMAT);
  const endDateUTC = timezoneToUtc(endDate, timezone)
    .format(DATETIME_FORMAT);
  const todStartUTC = timezoneToUtc(`${startDate.split(' ')[0]} ${todStart}`, timezone)
    .format(TIME_FORMAT_WITH_SECONDS);
  const todEndUTC = timezoneToUtc(`${startDate.split(' ')[0]} ${todEnd}`, timezone)
    .add(-1, 'seconds')
    .format(TIME_FORMAT_WITH_SECONDS);
  return getPPDDataURL(startDateUTC, endDateUTC, intersectionId, selectedDaysOfWeek,
    todStartUTC, todEndUTC);
};

const getAggDataApiUrl = (startDate: string, endDate: string, intersectionId: IntersectionId,
  timezone: string, direction: Direction, movement: Movement, selectedDaysOfWeek: DayOfWeek[],
  todStart: string, todEnd: string): string => {
  const startDateUTC = timezoneToUtc(startDate, timezone)
    .format(DATETIME_FORMAT);
  const endDateUTC = timezoneToUtc(endDate, timezone)
    .format(DATETIME_FORMAT);
  const todStartUTC = timezoneToUtc(`${startDate.split(' ')[0]} ${todStart}`, timezone)
    .format(TIME_FORMAT_WITH_SECONDS);
  const todEndUTC = timezoneToUtc(`${startDate.split(' ')[0]} ${todEnd}`, timezone)
    .add(-1, 'seconds')
    .format(TIME_FORMAT_WITH_SECONDS);
  return getAggDataURL(startDateUTC, endDateUTC, [intersectionId], selectedDaysOfWeek,
    todStartUTC, todEndUTC);
};

export default PPDPage;
